import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle, Loader2, Lock, XCircle } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { noIndexMeta } from "@/lib/seo";
import { authClient } from "../lib/auth-client";

const RESET_GRID_CELLS = Array.from(
	{ length: 80 },
	(_, index) => `auth-grid-cell-${index}`,
);

export const Route = createFileRoute("/reset-password")({
	validateSearch: (search: Record<string, unknown>) => ({
		token: (search.token as string) || "",
		error: (search.error as string) || "",
	}),
	component: ResetPasswordPage,
	head: () => ({
		meta: [{ title: "AgentReacher | Choose a new password" }, ...noIndexMeta()],
	}),
});

function getPasswordStrength(password: string): {
	score: number;
	label: string;
	color: string;
} {
	let score = 0;
	if (password.length >= 8) score++;
	if (password.length >= 12) score++;
	if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
	if (/\d/.test(password)) score++;
	if (/[^a-zA-Z0-9]/.test(password)) score++;

	if (score <= 1) return { score: 1, label: "Weak", color: "bg-red-500" };
	if (score <= 2) return { score: 2, label: "Fair", color: "bg-orange-500" };
	if (score <= 3) return { score: 3, label: "Medium", color: "bg-yellow-500" };
	if (score <= 4) return { score: 4, label: "Strong", color: "bg-green-500" };
	return { score: 5, label: "Very Strong", color: "bg-green-600" };
}

function ResetPasswordPage() {
	const { token, error: tokenError } = Route.useSearch();
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [invalidToken, setInvalidToken] = useState(!token || !!tokenError);
	const passwordId = useId();
	const confirmPasswordId = useId();

	const passwordStrength = useMemo(
		() => getPasswordStrength(password),
		[password],
	);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (password !== confirmPassword) {
			setError("Passwords do not match");
			return;
		}

		if (password.length < 8) {
			setError("Password must be at least 8 characters");
			return;
		}

		setLoading(true);

		try {
			const { error: apiError } = await authClient.resetPassword({
				newPassword: password,
				token,
			});
			if (apiError) {
				const message = apiError.message || "Failed to reset password";
				if (
					message.toLowerCase().includes("token") ||
					message.toLowerCase().includes("expired")
				) {
					setInvalidToken(true);
				} else {
					setError(message);
				}
			} else {
				setSuccess(true);
			}
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to reset password";
			if (
				message.toLowerCase().includes("token") ||
				message.toLowerCase().includes("expired")
			) {
				setInvalidToken(true);
			} else {
				setError(message);
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />
			<div className="mx-auto flex w-full max-w-6xl flex-1 px-4 sm:px-6">
				<div className="relative flex flex-1 flex-col overflow-hidden bg-border [--auth-row-size:4.75rem] sm:[--auth-row-size:5.25rem]">
					<div
						aria-hidden="true"
						className="pointer-events-none absolute inset-0 grid grid-cols-4 gap-px sm:grid-cols-8"
						style={{ gridAutoRows: "calc(var(--auth-row-size) - 1px)" }}
					>
						{RESET_GRID_CELLS.map((cellId) => (
							<div key={cellId} className="rounded-md bg-white dark:bg-black" />
						))}
					</div>
					<main className="relative pb-16 pt-16 sm:pb-24 sm:pt-24">
						<div className="grid w-full grid-cols-4 sm:grid-cols-8">
							<div className="col-span-4 space-y-6 rounded-md bg-white dark:bg-black px-6 py-16 shadow-[0_0_0_1px_var(--border)] sm:col-start-3 sm:col-end-7 sm:px-8">
								{success ? (
									<>
										<div className="flex justify-center">
											<div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
												<CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
											</div>
										</div>
										<div className="space-y-1 text-center">
											<h1 className="text-2xl font-semibold tracking-[-0.04em]">
												Password updated
											</h1>
											<p className="text-sm text-muted-foreground">
												Your password has been reset successfully.
											</p>
										</div>
										<Link
											to="/login"
											className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors"
										>
											Sign in
										</Link>
									</>
								) : invalidToken ? (
									<>
										<div className="flex justify-center">
											<div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
												<XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
											</div>
										</div>
										<div className="space-y-1 text-center">
											<h1 className="text-2xl font-semibold tracking-[-0.04em]">
												Reset link expired
											</h1>
											<p className="text-sm text-muted-foreground">
												This password reset link is invalid or has expired.
											</p>
										</div>
										<Link
											to="/forgot-password"
											className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors"
										>
											Request a new link
										</Link>
									</>
								) : (
									<>
										<div className="space-y-1">
											<h1 className="text-2xl font-semibold tracking-[-0.04em]">
												Set a new password
											</h1>
											<p className="text-sm text-muted-foreground">
												Choose a strong password for your account.
											</p>
										</div>

										{error && (
											<div className="rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
												{error}
											</div>
										)}

										<form onSubmit={handleSubmit} className="space-y-4">
											<div>
												<label
													htmlFor={passwordId}
													className="mb-2 block text-sm font-medium text-foreground"
												>
													New password
												</label>
												<div className="relative">
													<Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
													<input
														id={passwordId}
														type="password"
														autoComplete="new-password"
														value={password}
														onChange={(e) => setPassword(e.target.value)}
														required
														className="w-full rounded-md border border-border bg-muted py-2.5 pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
														placeholder="Enter a new password"
													/>
												</div>
												{password && (
													<div className="mt-2">
														<div className="mb-1 flex gap-1">
															{[1, 2, 3, 4, 5].map((level) => (
																<div
																	key={level}
																	className={`h-1 flex-1 rounded-full transition-colors ${
																		level <= passwordStrength.score
																			? passwordStrength.color
																			: "bg-muted"
																	}`}
																/>
															))}
														</div>
														<p className="text-right text-xs text-muted-foreground">
															{passwordStrength.label}
														</p>
													</div>
												)}
											</div>

											<div>
												<label
													htmlFor={confirmPasswordId}
													className="mb-2 block text-sm font-medium text-foreground"
												>
													Confirm password
												</label>
												<div className="relative">
													<Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
													<input
														id={confirmPasswordId}
														type="password"
														autoComplete="new-password"
														value={confirmPassword}
														onChange={(e) => setConfirmPassword(e.target.value)}
														required
														className="w-full rounded-md border border-border bg-muted py-2.5 pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
														placeholder="Confirm your new password"
													/>
												</div>
												{confirmPassword && password !== confirmPassword && (
													<p className="mt-1 text-xs text-destructive">
														Passwords do not match
													</p>
												)}
											</div>

											<button
												type="submit"
												disabled={loading || password !== confirmPassword}
												className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors disabled:cursor-not-allowed disabled:opacity-50"
											>
												{loading ? (
													<>
														<Loader2 className="h-4 w-4 animate-spin" />
														Resetting...
													</>
												) : (
													"Reset password"
												)}
											</button>
										</form>
									</>
								)}
							</div>
						</div>
					</main>
				</div>
			</div>
			<PublicFooter />
		</div>
	);
}
